"use client";
import Spinner from "@/components/ui/Spinner";
import ViewportHeightWrapper from "@/components/ViewportHeightWrapper";
import { useDebounce } from "@/hooks/useDebounce";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { getCdnImagePath } from "@/utils/bus/getCdnImagePath";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const GenericAutoComplete = ({
  id,
  label,
  customInputPlaceholder,
  value,
  onSelect,
  onClearSelection,
  searchFunction,
  placeholder,
  placeholderPortalInput,
  error,
  translationNamespace = "common",
  renderDefaultContent,
  renderSearchResults,
  renderNoResults,
  onOpen,
  debounce = 555,
  inputClassName = "",
  containerClassName = "",
  labelClassName = "",
  mobileContainerClassName = "",
  mobileIconClassName = "",
  viewportHeightWrapperActive = false,
  errorMessage,
  disableAnimation = true,
}) => {
  const isMobile = useIsMobile();
  const t = useTranslationDetector(translationNamespace);

  const [inputValue, setInputValue] = useState(value || "");
  const [portalInputValue, setPortalInputValue] = useState("");

  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isMobileFocused, setIsMobileFocused] = useState(false);

  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [shouldRenderPortal, setShouldRenderPortal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const [shouldRenderWebDropdown, setShouldRenderWebDropdown] = useState(false);
  const [isWebDropdownAnimating, setIsWebDropdownAnimating] = useState(false);

  const escapePressed = useKeyPressed("Escape");

  const debouncedDesktopValue = useDebounce(inputValue, debounce);
  const debouncedPortalValue = useDebounce(portalInputValue, debounce);

  const ref = useRef(null);
  const mobileRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const hiddenRef = useRef(null);

  const portalRootRef = useRef(null);
  const lockedScrollYRef = useRef(0);

  const skipNextSearchRef = useRef(false);
  const lastQueryRef = useRef("");

  const handleClickOutside = useCallback(() => {
    if (isMobile && shouldRenderPortal) return;

    if (!isMobile && isOpen) {
      setIsFocused(false);
      setIsOpen(false);
    }
  }, [isMobile, isOpen, shouldRenderPortal]);

  useEffect(() => {
    if (escapePressed && isOpen) {
      setIsFocused(false);
      setIsOpen(false);
    }
  }, [escapePressed, isOpen]);

  useOnClickOutside(ref, handleClickOutside);

  useEffect(() => {
    if (isOpen && isMobile) {
      setShouldRenderPortal(true);

      if (disableAnimation) {
        setIsAnimating(true);
      } else {
        const timer = setTimeout(() => setIsAnimating(true), 10);
        return () => clearTimeout(timer);
      }
    } else if (!isOpen && isMobile) {
      setIsAnimating(false);

      if (disableAnimation) {
        setShouldRenderPortal(false);
      } else {
        const timer = setTimeout(() => setShouldRenderPortal(false), 300);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, isMobile, disableAnimation]);

  useEffect(() => {
    if (isMobile && isOpen && !isAnimating) {
      hiddenRef.current?.focus();

      setTimeout(
        () => {
          if (mobileRef.current) {
            mobileRef.current.focus();
            mobileRef.current.select();
          }
        },
        disableAnimation ? 100 : 500,
      );
    }
  }, [isMobile, isOpen, isAnimating, disableAnimation]);

  useEffect(() => {
    if (isOpen && !isMobile) {
      setShouldRenderWebDropdown(true);
      const timer = setTimeout(() => setIsWebDropdownAnimating(true), 10);
      return () => clearTimeout(timer);
    } else if (!isOpen && !isMobile) {
      setIsWebDropdownAnimating(false);
      const timer = setTimeout(() => setShouldRenderWebDropdown(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (!isMobile || !shouldRenderPortal) return;

    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    lockedScrollYRef.current = scrollY;

    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = "-" + scrollY + "px";
    document.body.style.width = "100%";

    const preventTouchMove = (e) => {
      const root = portalRootRef.current;
      if (root && root.contains(e.target)) return;
      e.preventDefault();
    };
    document.addEventListener("touchmove", preventTouchMove, {
      passive: false,
    });

    return () => {
      document.removeEventListener("touchmove", preventTouchMove);

      const prevScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = "auto";

      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;

      const y = lockedScrollYRef.current;

      window.scrollTo(0, y);
      requestAnimationFrame(() => {
        window.scrollTo(0, y);
        requestAnimationFrame(() => {
          document.documentElement.style.scrollBehavior = prevScrollBehavior;
        });
      });
    };
  }, [isMobile, shouldRenderPortal]);

  useEffect(() => {
    if (!isMobile || !shouldRenderPortal) return;

    const root = portalRootRef.current;
    const input = mobileRef.current;
    if (!root || !input) return;

    const onTouchStart = (e) => {
      if (document.activeElement !== input) return;
      if (input.contains(e.target)) return;
      input.blur();
    };

    root.addEventListener("touchstart", onTouchStart, true);
    return () => root.removeEventListener("touchstart", onTouchStart, true);
  }, [isMobile, shouldRenderPortal]);

  const handleSearch = useCallback(
    async (query) => {
      if (!searchFunction || !query || query.trim() === "") {
        setSearchResults(null);
        return;
      }

      try {
        setIsLoading(true);
        lastQueryRef.current = query;
        const results = await searchFunction(query);
        if (lastQueryRef.current === query) {
          setSearchResults(results);
        }
      } catch (err) {
        if (lastQueryRef.current === query) {
          setSearchResults(null);
        }
        // eslint-disable-next-line no-console
        console.error("Search error:", err);
      } finally {
        if (lastQueryRef.current === query) {
          setIsLoading(false);
        }
      }
    },
    [searchFunction],
  );

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    skipNextSearchRef.current = false;
  }, []);

  const handleMobileChange = useCallback((e) => {
    const newValue = e.target.value;
    setPortalInputValue(newValue);
    skipNextSearchRef.current = false;
  }, []);

  useEffect(() => {
    const debouncedValue = isMobile
      ? debouncedPortalValue
      : debouncedDesktopValue;

    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }

    if (debouncedValue && debouncedValue.trim() !== "") {
      handleSearch(debouncedValue);
    } else {
      setSearchResults(null);
    }
  }, [isMobile, debouncedDesktopValue, debouncedPortalValue, handleSearch]);

  useEffect(() => {
    skipNextSearchRef.current = true;
    setInputValue(value || "");
  }, [value]);

  const onSelectItem = useCallback(
    (item) => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      skipNextSearchRef.current = true;

      setInputValue(item.value);
      setPortalInputValue("");
      setSearchResults(null);
      setIsFocused(false);
      setIsOpen(false);
      onSelect(item);
    },
    [onSelect],
  );

  const handlePortalClose = useCallback(() => {
    setIsFocused(false);
    setIsOpen(false);
  }, []);

  const onFocus = useCallback(
    (e) => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      setIsFocused(true);
      setIsOpen(true);

      if (onOpen) onOpen();

      e.target.select();
    },
    [onOpen],
  );

  const clearInput = useCallback(() => {
    setInputValue("");
    setSearchResults(null);
    if (onClearSelection) onClearSelection();
  }, [onClearSelection]);

  const clearMobileInput = useCallback(() => {
    setPortalInputValue("");
    setSearchResults(null);
    mobileRef.current?.focus();
  }, []);

  const containerClasses = useMemo(
    () =>
      clsx(
        "border-primary-weak h-[68px] border-b duration-300 ease-in-out md:h-[66px] md:rounded-lg md:border-0",
        isFocused && !error && "md:ring-primary md:ring-2",
        error && "rounded-lg ring-2 ring-red-500",
        containerClassName,
      ),
    [isFocused, error, containerClassName],
  );

  const labelClasses = useMemo(
    () =>
      clsx(
        "absolute left-[18px] font-medium transition-all duration-300 ease-in-out md:left-5",
        error ? "text-red-500" : "text-foreground-muted",
        isFocused || inputValue || (error && isMobile)
          ? "top-[11.5px] text-xs"
          : "top-1/2 -translate-y-1/2 text-base",
        labelClassName,
      ),
    [isFocused, inputValue, error, isMobile, labelClassName],
  );

  const inputClasses = useMemo(
    () =>
      clsx(
        "selection:bg-secondary-weak h-full w-full truncate rounded-lg border-0 px-[18px] font-medium transition-all duration-300 ease-in-out focus:ring-0 focus:outline-none md:px-5",
        isFocused || inputValue
          ? "pt-[26px] pb-[12px] md:pt-[31.5px] md:pb-[13.5px]"
          : "py-[12px] md:py-[22.5px]",
        inputClassName,
      ),
    [isFocused, inputValue, inputClassName],
  );

  const mobileContainerClasses = useMemo(
    () =>
      clsx(
        "flex h-[56px] items-center justify-between gap-4 rounded-lg border-2 px-4 transition-all duration-300 ease-in-out",
        error && "border-red-500",
        !error && isMobileFocused && "border-primary",
        !error && !isMobileFocused && "border-primary-weak",
        error && isMobileFocused && "border-red-500",
        mobileContainerClassName,
      ),
    [isMobileFocused, error, mobileContainerClassName],
  );

  const mobileIconClasses = useMemo(
    () =>
      clsx(
        "text-xl transition-all duration-300 ease-in-out",
        error
          ? "text-red-500"
          : isMobileFocused
            ? "text-primary"
            : "text-foreground-subtle",
        mobileIconClassName,
      ),
    [isMobileFocused, error, mobileIconClassName],
  );

  const renderContent = useCallback(() => {
    const renderProps = {
      onSelectItem,
      isLoading,
      inputValue,
      clearInput,
      onClosePortal: handlePortalClose,
    };

    if (searchResults !== null) {
      if (renderSearchResults) {
        return renderSearchResults({ ...renderProps, searchResults });
      }

      return searchResults?.length === 0 ? (
        renderNoResults ? (
          renderNoResults(renderProps)
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 p-10 text-sm">
            <Image
              src={getCdnImagePath("/saka/common/svg/telescope.svg")}
              alt="No results"
              width={60}
              height={60}
            />
            <div className="flex flex-col gap-2">
              <span className="text-center text-lg font-semibold">
                {t("noResultsFound")}
              </span>
              <span className="text-center text-sm">
                {t("changeSearchCriteria")}
              </span>
            </div>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-4 text-sm">
          {searchResults?.map((searchResult) => (
            <button
              className={clsx(
                "flex cursor-pointer items-start justify-between gap-6",
                !searchResult?.isCity && "pl-8",
              )}
              key={searchResult.id}
              onClick={() => onSelectItem(searchResult)}
            >
              <div className="flex items-center gap-4 text-left">
                {searchResult.isCity ? (
                  <FontAwesomeIcon icon={byPrefixAndName.far["location-dot"]} />
                ) : (
                  <FontAwesomeIcon icon={byPrefixAndName.fak["new-flight"]} />
                )}
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-center justify-start gap-[6px] font-semibold">
                    {searchResult.value}
                  </div>
                  <div className="text-foreground-muted">
                    {searchResult.label.split("-")[0]}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">{searchResult.code}</div>
            </button>
          ))}
        </div>
      );
    }

    if (renderDefaultContent) return renderDefaultContent(renderProps);
    return null;
  }, [
    searchResults,
    renderSearchResults,
    renderDefaultContent,
    renderNoResults,
    onSelectItem,
    isLoading,
    inputValue,
    clearInput,
    handlePortalClose,
    t,
  ]);

  const portalContainerClasses = useMemo(() => {
    if (disableAnimation) {
      return "mobile-portal fixed inset-0 z-50 flex flex-col overflow-hidden h-[100%] bg-white";
    }

    return clsx(
      "mobile-portal fixed inset-0 z-50 flex flex-col bg-white",
      "transform transition-all duration-300",
      isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
    );
  }, [isAnimating, disableAnimation]);

  const webDropdownClasses = useMemo(() => {
    return clsx(
      "web-dropdown shadow-xxl absolute top-full left-0 z-50 mt-2 w-[475px] rounded-lg bg-white p-5",
      "origin-top transform transition-all duration-200 ease-out",
      isWebDropdownAnimating
        ? "translate-y-0 scale-100 opacity-100"
        : "-translate-y-2 scale-95 opacity-0",
    );
  }, [isWebDropdownAnimating]);

  const handleBlur = useCallback(() => {
    if (isMobile && shouldRenderPortal) return;

    if (onClearSelection) {
      const currentInput = (inputValue ?? "").trim();
      const selectedValue = (value ?? "").trim();
      if (!currentInput || currentInput !== selectedValue) {
        onClearSelection();
      }
    }

    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);
      setIsOpen(false);
    }, 150);
  }, [isMobile, shouldRenderPortal, onClearSelection, inputValue, value]);

  const mobilInputFocus = () => {
    if (!isMobile) return;
    mobileRef.current?.select();
    setIsMobileFocused(true);
  };

  const mobilInputBlur = () => {
    if (!isMobile) return;
    setIsMobileFocused(false);

    if (onClearSelection) {
      const currentInput = (inputValue ?? "").trim();
      const selectedValue = (value ?? "").trim();
      if (!currentInput || currentInput !== selectedValue) {
        onClearSelection();
      }
    }
  };

  const mobileDrawerTrigger = () => {
    if (!disableAnimation && shouldRenderPortal && !isAnimating) return;

    setPortalInputValue("");
    setSearchResults(null);

    setIsOpen(true);
    if (onOpen) onOpen();
  };

  return (
    <>
      <div className="relative w-full" ref={ref}>
        <input
          ref={hiddenRef}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />
        <div className={containerClasses}>
          <div className="relative h-full">
            {label && (
              <label htmlFor={id} className={labelClasses}>
                {label}
              </label>
            )}

            {isMobile && (
              <button
                className="absolute top-0 left-0 z-10 h-full w-full bg-transparent"
                type="button"
                onClick={mobileDrawerTrigger}
              />
            )}

            <input
              type="text"
              value={inputValue}
              id={id}
              onChange={handleChange}
              className={inputClasses}
              autoComplete="off"
              placeholder={
                placeholder || (isFocused ? customInputPlaceholder : "")
              }
              onBlur={handleBlur}
              onFocus={onFocus}
              readOnly={isMobile}
            />

            {isLoading && (
              <Spinner className="text-primary absolute top-1/2 right-4 h-4 w-4 -translate-y-1/2 animate-spin" />
            )}
          </div>
        </div>

        {error && (
          <p className="text-danger absolute -mt-8 flex items-center gap-1 pl-4 md:mt-1 md:text-xs">
            <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
            <span>{error || errorMessage}</span>
          </p>
        )}

        {!isMobile && shouldRenderWebDropdown && (
          <div className={webDropdownClasses}>
            <ViewportHeightWrapper
              defaultMaxHeight={460}
              bottomPadding={32}
              enabled={viewportHeightWrapperActive}
            >
              {renderContent()}
            </ViewportHeightWrapper>
          </div>
        )}
      </div>

      {shouldRenderPortal &&
        isMobile &&
        createPortal(
          <div ref={portalRootRef} className={portalContainerClasses}>
            <div className="flex shrink-0 items-center justify-between px-4 py-5">
              <span className="w-full text-center text-lg font-semibold">
                {label}
              </span>
              <button onClick={handlePortalClose} className="self-end">
                <FontAwesomeIcon
                  icon={byPrefixAndName.far["xmark"]}
                  className="text-2xl"
                />
              </button>
            </div>

            <div className="h-[calc(100%-60px)] flex-1 px-4 pb-4">
              <div className="flex h-full flex-col gap-6">
                <div className={mobileContainerClasses}>
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["search"]}
                    className={mobileIconClasses}
                  />
                  <input
                    type="text"
                    value={portalInputValue ?? ""}
                    onChange={handleMobileChange}
                    onFocus={mobilInputFocus}
                    onBlur={mobilInputBlur}
                    placeholder={placeholderPortalInput}
                    ref={mobileRef}
                    className="selection:bg-secondary-weak w-3/4 border-0 px-0 py-3 focus:ring-0 focus:outline-none"
                    autoComplete="off"
                  />

                  <div className="flex h-6 w-6 items-center justify-center">
                    {isLoading ? (
                      <Spinner className="text-primary h-4 w-4 animate-spin" />
                    ) : portalInputValue ? (
                      <button
                        onClick={clearMobileInput}
                        className="bg-background-400 text-foreground-muted flex h-6 w-6 items-center justify-center overflow-hidden rounded-full"
                        aria-label="Temizle"
                      >
                        <FontAwesomeIcon
                          icon={byPrefixAndName.far["xmark"]}
                          className="text-sm"
                        />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="scrollbar-hide min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain">
                  {renderContent()}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default GenericAutoComplete;
